import React, { FC } from 'react'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import {
  BetmenListItemCard,
  BetmenListItemCardCell,
} from '../../../../../common/components'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { Stack, Typography } from '@mui/material'
import { useExtraBetmenListItemCardStyle } from '../../../../../utils'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useTreatmentPanelData } from '../TreatmentPanel/TreatmentPanelProvider'
import { TreatmentReferralItemComponentViewModel } from '../../types/treatmentItem.types'
import { DeleteReferralButton } from '../../../DoctorPatients/components/PatientReferrals/DeleteReferralButton'

interface Props {
  vm: TreatmentReferralItemComponentViewModel
}

const TreatmentReferralItemComponent: FC<React.PropsWithChildren<Props>> = ({
  vm: { referral },
}) => {
  const {
    showPastTreatmentItems,
    onViewReferral,
    setHoveredTreatmentItem,
    refetchTreatment,
  } = useTreatmentPanelData()
  const classes = useExtraBetmenListItemCardStyle()
  const { t } = useTranslation()

  const isPast = dayjs().startOf('day') > dayjs(referral.expirationDate)

  return !showPastTreatmentItems && isPast ? null : (
    <BetmenListItemCard
      className={clsx(classes.container, {
        [classes.past]: isPast,
      })}
      click={() => onViewReferral(referral)}
      onMouseEnter={() => {
        setHoveredTreatmentItem({
          id: referral.id,
          date: dayjs(referral.expirationDate),
        })
      }}
      onMouseLeave={() => {
        setHoveredTreatmentItem(null)
      }}
    >
      <BetmenListItemCardCell gridSize={1}>
        <DescriptionOutlinedIcon fontSize="large" />
      </BetmenListItemCardCell>
      <BetmenListItemCardCell gridSize={4}>
        {referral.name}
      </BetmenListItemCardCell>
      <BetmenListItemCardCell
        typographyProps={{ fontWeight: 400 }}
        gridSize={3}
      >
        {t('common:formattedNameFull', {
          title: referral.doctor.title,
          firstName: referral.doctor.firstName,
          lastName: referral.doctor.lastName,
        })}
      </BetmenListItemCardCell>
      <BetmenListItemCardCell
        typographyProps={{ fontWeight: 400 }}
        gridSize={3}
      >
        <Stack direction="row" gap={1}>
          {t(`doctor:referral.status.${referral.status.toLowerCase()}`)}{' '}
          <Typography variant="body2" color="textSecondary">
            {t('common:intlDateFormatted', { date: referral.expirationDate })}
          </Typography>
        </Stack>
      </BetmenListItemCardCell>
      <BetmenListItemCardCell>
        {referral.isDeletable && (
          <DeleteReferralButton
            referralId={referral.id}
            refetch={refetchTreatment}
          />
        )}
      </BetmenListItemCardCell>
    </BetmenListItemCard>
  )
}

const TreatmentReferralItem = React.memo(TreatmentReferralItemComponent)

export { TreatmentReferralItem }
